/**
 * Load number detection utilities for Front Plugin
 * Extracts load numbers from conversation tags and subject lines
 */

export interface LoadDetectionResult {
  loadNumber: string | null
  matchType: 'tag' | 'subject' | 'none'
  confidence: 'high' | 'medium' | 'low'
}

/**
 * Extract load number from conversation tags
 * Looks for patterns like: "load:12345", "B18-12345", "#12345"
 * Load numbers can be any length (customer-defined), minimum 4 digits
 */
export const extractLoadFromTags = (tags: string[]): string | null => {
  if (!tags || tags.length === 0) return null

  for (const tag of tags) {
    const normalizedTag = tag.toLowerCase().trim()

    // Pattern: load:12345 or load-12345 (4+ digits)
    const loadPrefixMatch = normalizedTag.match(/^load[:\-_]?(\d{4,})$/i)
    if (loadPrefixMatch) return loadPrefixMatch[1]

    // Pattern: B18-12345 (4+ digits)
    const b18Match = normalizedTag.match(/^b18[:\-_](\d{4,})$/i)
    if (b18Match) return b18Match[1]

    // Pattern: #12345 (just digits with hash, 4+ digits)
    const hashMatch = normalizedTag.match(/^#(\d{4,})$/)
    if (hashMatch) return hashMatch[1]

    // Pattern: Pure digits (5+ digits, likely a load number)
    const pureDigits = normalizedTag.match(/^(\d{5,})$/)
    if (pureDigits) return pureDigits[1]
  }

  return null
}

/**
 * Extract load number from email subject line
 * Uses regex to find potential load numbers
 * Load numbers can be any length (customer-defined), minimum 4 digits
 * Captures all consecutive digits until a non-digit character (space, symbol, etc.)
 */
export const extractLoadFromSubject = (subject: string): string | null => {
  if (!subject) return null

  // Pattern 1: "Load #12345" or "Load 12345" or "Load# 12345"
  // Captures all digits after "load" keyword until non-digit
  // \b keeps words like payload, upload and download from counting as "load"
  const loadMatch = subject.match(/\bload\s*#?\s*(\d{4,})/i)
  if (loadMatch) return loadMatch[1]

  // Pattern 2: "B18-12345" or "B18 12345"
  const b18Match = subject.match(/\bb18[\s\-_]*(\d{4,})/i)
  if (b18Match) return b18Match[1]

  // Pattern 3: "#12345" with hash prefix (4+ digits)
  const hashMatch = subject.match(/#(\d{4,})/)
  if (hashMatch) return hashMatch[1]

  // Pattern 4: Standalone 5+ digit number (lower confidence)
  // Only match if followed by non-digit (space, symbol, end of string)
  const standaloneMatch = subject.match(/\b(\d{5,})(?=\D|$)/)
  if (standaloneMatch) return standaloneMatch[1]

  return null
}

/**
 * Main detection function - checks tags first, then subject
 */
export const detectLoadNumber = (
  tags: string[],
  subject: string
): LoadDetectionResult => {
  // Priority 1: Check tags (highest confidence)
  const tagMatch = extractLoadFromTags(tags)
  if (tagMatch) {
    return {
      loadNumber: tagMatch,
      matchType: 'tag',
      confidence: 'high'
    }
  }

  // Priority 2: Check subject line
  const subjectMatch = extractLoadFromSubject(subject)
  if (subjectMatch) {
    // Determine confidence based on match pattern
    const hasLoadKeyword = /\bload/i.test(subject)
    const hasB18Prefix = /\bb18/i.test(subject)

    return {
      loadNumber: subjectMatch,
      matchType: 'subject',
      confidence: hasLoadKeyword || hasB18Prefix ? 'high' : 'medium'
    }
  }

  return {
    loadNumber: null,
    matchType: 'none',
    confidence: 'low'
  }
}
